import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Field, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Separator } from "@libs/zapi-ui/ds/components/separator";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Textarea } from "@libs/zapi-ui/ds/components/textarea";
import { BuildingIcon, UploadSimpleIcon, TrashSimpleIcon } from "@phosphor-icons/react";
import { useRef, useState, useEffect, useId } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../const/const";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUpdateOrganization } from "../services/useUpdateOrganization";
import { useDeleteOrganizationLogo } from "../services/useDeleteOrganizationLogo";
import { useAiModelSettings } from "../services/useAiModelSettings";
import { useOrganization } from "../services/useOrganization";

type TOrganizationData = {
  organizationName: string;
  customerInstructions: string;
  organizationLogoUrl: string | null;
};

function AIModelSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { aiModelSettings, isLoading } = useAiModelSettings();
  const { isLabs } = useMeStore();
  const inputId = useId();

  if (isLoading) return null;
  if (!aiModelSettings) return null;
  if (isLabs) return null;

  const { isUsingOrganizationOverride, currentModel } = aiModelSettings;

  const displayValue = isUsingOrganizationOverride ? currentModel : "Innovera Default";

  return (
    <div className="flex flex-col gap-1">
      <Field className="gap-2">
        <FieldLabel htmlFor={inputId}>{t("general.aiModel")}</FieldLabel>
        <Input id={inputId} className="w-[320px]" value={displayValue} disabled={true} />
      </Field>
    </div>
  );
}

export function GeneralSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { organizationId: userOrganizationId, isLabs } = useMeStore();
  const { currentOrganizationId, updateOrganizationName } = useOrganizationStore();
  const { data: organizationData, refetch: refetchOrganization, isLoading: isLoadingOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const orgNameId = useId();
  const instructionsId = useId();

  // Use currentOrganizationId if available (for superAdmin), otherwise fall back to user's organizationId
  const organizationId = currentOrganizationId || userOrganizationId;

  const { mutate: updateOrganization, isPending: isSaving } = useUpdateOrganization();
  const { mutate: deleteOrganizationLogo, isPending: isDeleting } = useDeleteOrganizationLogo();

  const [orgName, setOrgName] = useState("");
  const [customerInstructionsValue, setCustomerInstructionsValue] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (organizationData) {
      setOrgName(organizationData.organizationName ?? "");
    }
  }, [organizationData]);

  useEffect(() => {
    if (organizationData) {
      setCustomerInstructionsValue(organizationData.customerInstructions);
    }
  }, [organizationData]);

  const isChanged =
    orgName !== (organizationData?.organizationName ?? "") ||
    customerInstructionsValue !== (organizationData?.customerInstructions ?? "");

  function fileClickHandler() {
    fileInputRef.current?.click();
  }

  function validateFile(file: File): string | null {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast.error(t("general.invalidFileType"));
      return "Invalid file type. Only JPG, PNG and WebP files are allowed.";
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("general.fileTooLarge"));
      return "File size must not exceed 5MB.";
    }

    return null;
  }

  async function fileChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleRemovePhoto = async () => {
    if (!organizationId) {
      toast.error(t("general.orgIdMissing"));
      return;
    }

    setUploadError(null);

    deleteOrganizationLogo(
      { organizationId },
      {
        onSuccess: async () => {
          // Optimistically update query cache to remove logo URL
          queryClient.setQueryData<TOrganizationData>(["organization", currentOrganizationId], (old) => {
            if (!old) return old;
            return { ...old, organizationLogoUrl: null };
          });

          toast.success(t("general.logoRemoved"));
          await refetchOrganization();
        },
        onError: (error) => {
          console.error("Failed to remove organization logo:", error);
          const err = error as { response?: { data?: { message?: string } } };
          setUploadError(err.response?.data?.message || "Failed to remove logo. Please try again.");
          toast.error(t("general.logoRemoveFailed"));
        }
      }
    );
  };

  const handleSaveChanges = async () => {
    if (!organizationId) {
      toast.error(t("general.orgIdMissing"));
      return;
    }

    setUploadError(null);

    updateOrganization(
      {
        organizationId,
        name: orgName,
        file: selectedFile ?? undefined,
        customerInstructions: customerInstructionsValue
      },
      {
        onSuccess: async () => {
          // Optimistically update query cache immediately to reset isChanged
          // Note: Logo URL will be updated after refetch, but we update name and instructions immediately
          queryClient.setQueryData<TOrganizationData>(["organization", currentOrganizationId], (old) => {
            if (!old) return old;
            return {
              ...old,
              organizationName: orgName,
              customerInstructions: customerInstructionsValue
            };
          });

          // Update organization name in the organization store (for the dropdown)
          if (organizationId) {
            updateOrganizationName(organizationId, orgName);
          }

          // Update local state immediately with saved values
          setOrgName(orgName);
          setCustomerInstructionsValue(customerInstructionsValue);

          toast.success(t("general.updated"));
          await refetchOrganization();

          setSelectedFile(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
        onError: (error) => {
          toast.error(t("general.updateFailed"));
          console.error("Failed to update organization:", error);
        }
      }
    );
  };

  if (isLoadingOrganization) {
    return (
      <div className="flex flex-col gap-[18px] bg-white">
        <p className="typo-h6 text-innoveraZinc-900">{t("general.heading")}</p>
        <div className="flex items-center gap-2">
          <Spinner aria-label={t("common:loading")} className="size-5 text-muted-foreground" />
          <p className="typo-caption text-innoveraZinc-500">{t("general.loading")}</p>
        </div>
      </div>
    );
  }

  const organizationLogoUrl = organizationData?.organizationLogoUrl || null;

  return (
    <div className="flex flex-col gap-[18px] bg-white">
      <p className="typo-h6 text-innoveraZinc-900">{t("general.heading")}</p>

      <div className="flex flex-col gap-8">
        <div className="flex gap-6">
          <button
            data-testid="general-settings-logo-button"
            className={cn(
              "border-none outline-hidden cursor-pointer flex items-center justify-center",
              "w-[88px] h-[88px] rounded-full bg-innoveraZinc-900 overflow-hidden"
            )}
            onClick={fileClickHandler}
          >
            {(previewUrl || organizationLogoUrl) && (
              <img
                src={previewUrl || organizationLogoUrl || ""}
                alt="organization logo"
                className="w-full h-full object-cover"
              />
            )}
            {!previewUrl && !organizationLogoUrl && <BuildingIcon className="text-white w-[35px] h-[35px]" />}
          </button>
          <input
            data-testid="general-settings-logo-input"
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={fileChangeHandler}
          />

          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                data-testid="general-settings-upload-logo-button"
                onClick={fileClickHandler}
                disabled={isSaving || isDeleting}
              >
                <UploadSimpleIcon />
                {t("general.uploadLogo")}
              </Button>
              {organizationLogoUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="general-settings-remove-logo-button"
                  onClick={handleRemovePhoto}
                  disabled={isSaving || isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                      {t("general.removing")}
                    </>
                  ) : (
                    <>
                      <TrashSimpleIcon />
                      {t("general.removeLogo")}
                    </>
                  )}
                </Button>
              )}
            </div>
            <div>
              <p className="typo-caption text-innoveraZinc-500">{t("general.logoHintSize")}</p>
              <p className="typo-caption text-innoveraZinc-500">{t("general.logoHintFormat")}</p>
              {uploadError && <p className="typo-caption text-red-500 mt-1">{uploadError}</p>}
            </div>
          </div>
        </div>

        <Separator />

        <div className="flex gap-4">
          <Field className="gap-2">
            <FieldLabel htmlFor={orgNameId}>{t("general.orgName")}</FieldLabel>
            <Input
              id={orgNameId}
              data-testid="general-settings-organization-name-input"
              className="w-[250px]"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              disabled={isSaving || isDeleting}
            />
          </Field>
        </div>

        <AIModelSection />

        {isLabs && (
          <div className="flex flex-col gap-1">
            <Field className="gap-2 inline-flex w-fit">
              <FieldLabel htmlFor={instructionsId}>{t("general.orgInstructions")}</FieldLabel>
              <Textarea
                id={instructionsId}
                data-testid="general-settings-organization-instructions-input"
                className="w-full min-w-[800px] max-w-[800px] min-h-[200px]"
                value={customerInstructionsValue}
                onChange={(e) => setCustomerInstructionsValue(e.target.value)}
                disabled={isSaving || isDeleting}
                rows={6}
                placeholder={t("general.orgInstructionsPlaceholder")}
              />
            </Field>
          </div>
        )}

        <Button
          type="button"
          data-testid="general-settings-save-button"
          className="w-fit"
          onClick={handleSaveChanges}
          disabled={isSaving || isDeleting || !isChanged}
        >
          {isSaving ? (
            <>
              <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
              {t("common.saving")}
            </>
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}
